import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import AddIcon from "@mui/icons-material/Add";
import PrintIcon from "@mui/icons-material/PrintOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import PageHeader from "components/PageHeader";
import DataTable from "components/DataTable";
import ConfirmDialog from "components/ConfirmDialog";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";

import useNewConvertData from "layouts/tables/data/newConvertTableData";

function NewConverts() {
  const {
    columns,
    rows,
    isLoading,
    error,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    sessionsModalOpen,
    setSessionsModalOpen,
    selectedConvert,
    form,
    setForm,
    handleAdd,
    handleSubmit,
    handleDeleteConfirm,
    isSaving,
    newSession,
    setNewSession,
    handleAddSession,
    isSavingSession,
  } = useNewConvertData();

  if (isLoading) {
    return <Typography sx={{ py: 6 }}>Loading new converts...</Typography>;
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 6 }}>
        Error: {error}
      </Typography>
    );
  }

  const sessions = selectedConvert?.originalData?.sessions || [];

  return (
    <Box>
      <PageHeader
        title="New Converts"
        subtitle="Enroll new converts, track their class attendance, and graduate them to full membership."
        actions={
          <>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
              New Convert
            </Button>
            <IconButton onClick={handlePrint} sx={{ border: 1, borderColor: "divider" }}>
              <PrintIcon fontSize="small" />
            </IconButton>
          </>
        }
      />

      <Card>
        <DataTable
          table={{ columns, rows }}
          isSorted
          entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
          showTotalEntries
          canSearch
          searchLabel="Search new converts..."
        />
      </Card>

      <FormDialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={form.id ? "Edit New Convert" : "New Convert"}
        onSave={handleSubmit}
        saveLabel={isSaving ? "Saving..." : form.id ? "Save Changes" : "Enroll Convert"}
        saveDisabled={
          !form.first_name.trim() || !form.surname.trim() || !form.conversion_date || isSaving
        }
        loading={isSaving}
        maxWidth="sm"
      >
        <FormSection>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="First Name"
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              fullWidth
              margin="normal"
              required
            />
            <TextField
              label="Surname"
              value={form.surname}
              onChange={(e) => setForm({ ...form, surname: e.target.value })}
              fullWidth
              margin="normal"
              required
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              fullWidth
              margin="normal"
            />
          </Box>
          <TextField
            label="Address"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            fullWidth
            margin="normal"
          />
        </FormSection>

        <FormSection>
          <TextField
            label="Conversion Date"
            type="date"
            value={form.conversion_date}
            onChange={(e) => setForm({ ...form, conversion_date: e.target.value })}
            fullWidth
            margin="normal"
            required
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select
            label="Status"
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            fullWidth
            margin="normal"
          >
            <MenuItem value="enrolled">Enrolled</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="graduated">Graduated</MenuItem>
            <MenuItem value="dropped">Dropped</MenuItem>
          </TextField>
          <TextField
            label="Notes"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            fullWidth
            margin="normal"
            multiline
            minRows={3}
          />
        </FormSection>
      </FormDialog>

      <FormDialog
        open={sessionsModalOpen}
        onClose={() => setSessionsModalOpen(false)}
        title={`Class Sessions — ${selectedConvert?.firstName || ""} ${
          selectedConvert?.surname || ""
        }`}
        onSave={handleAddSession}
        saveLabel={isSavingSession ? "Saving..." : "Add Session"}
        saveDisabled={!newSession.session_date || isSavingSession}
        loading={isSavingSession}
        maxWidth="sm"
      >
        <FormSection>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Session Date"
              type="date"
              value={newSession.session_date}
              onChange={(e) => setNewSession({ ...newSession, session_date: e.target.value })}
              fullWidth
              margin="normal"
              required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Topic"
              value={newSession.topic}
              onChange={(e) => setNewSession({ ...newSession, topic: e.target.value })}
              fullWidth
              margin="normal"
            />
          </Box>
          <TextField
            label="Notes"
            value={newSession.notes}
            onChange={(e) => setNewSession({ ...newSession, notes: e.target.value })}
            fullWidth
            margin="normal"
            multiline
            minRows={2}
          />
        </FormSection>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Attendance history ({sessions.length})
        </Typography>
        {sessions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No sessions recorded yet.
          </Typography>
        ) : (
          sessions.map((s) => (
            <Box
              key={s.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                py: 1,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {new Date(s.session_date).toLocaleDateString()} {s.topic ? `— ${s.topic}` : ""}
                </Typography>
                {s.notes && (
                  <Typography variant="caption" color="text.secondary">
                    {s.notes}
                  </Typography>
                )}
              </Box>
              <DeleteOutlineIcon fontSize="small" color="disabled" />
            </Box>
          ))
        )}
      </FormDialog>

      <ConfirmDialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${selectedConvert?.firstName} ${selectedConvert?.surname}"?`}
        confirmLabel="Delete"
      />
    </Box>
  );
}

export default NewConverts;
